import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Heart, 
  MessageCircle, 
  CheckCircle, 
  Clock,
  Send,
  Lock,
  Unlock,
  X,
  ArrowRight,
  Loader2,
  Shield,
  ChevronRight,
  Sparkles,
  AlertTriangle,
  Star
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ============== MATCH NOTIFICATION (premium) ==============
const MatchReveal = ({ match, onDismiss }) => {
  const [visible, setVisible] = useState(false);
  const reveal = match?.reveal || {};

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  return (
    <div className={`fixed inset-0 z-50 bg-[#0B0B0B]/95 backdrop-blur-lg flex items-center justify-center px-6 transition-opacity duration-700 ${visible ? 'opacity-100' : 'opacity-0'}`}
         data-testid="match-reveal-overlay">
      <div className="max-w-md w-full text-center">
        {/* Pulsing rings */}
        <div className="relative w-28 h-28 mx-auto mb-10">
          <div className="absolute inset-0 border border-[#8B1A1A]/20 rounded-full animate-ping" style={{ animationDuration: '2s' }} />
          <div className="absolute inset-3 border border-[#8B1A1A]/40 rounded-full animate-pulse" />
          <div className="absolute inset-0 flex items-center justify-center">
            <Heart size={36} className="text-[#8B1A1A]" />
          </div>
        </div>

        <p className="label-premium mb-4 tracking-[0.3em]">Conexion mutua</p>
        <h2 className="text-3xl font-serif text-[#F5F0E6] mb-4">
          {reveal.headline || 'Algo especial sucedio'}
        </h2>
        <p className="text-[#8A8A8A] text-lg mb-3 leading-relaxed">
          {reveal.subline || `Esta experiencia tambien fue significativa para ${match?.partner?.first_name}.`}
        </p>
        <p className="text-[#666] text-sm mb-10">
          {match?.event_title}
        </p>

        <button
          onClick={onDismiss}
          className="btn-wine inline-flex items-center gap-3 px-10 py-4"
          data-testid="match-reveal-continue-btn"
        >
          Explorar conexion
          <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
};

// ============== CONFIRMATION CARD ==============
const ConfirmationCard = ({ match, onConfirm, loading }) => (
  <div className="card-premium p-8 max-w-md mx-auto text-center" data-testid={`confirm-card-${match.match_id}`}>
    <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#8B1A1A] to-[#5a1111] flex items-center justify-center text-2xl font-serif text-[#F5F0E6]">
      {match.partner?.first_name?.[0]}
    </div>
    <h3 className="text-xl font-serif text-[#F5F0E6] mb-2">{match.partner?.first_name}</h3>
    {match.partner?.occupation && (
      <p className="text-[#8A8A8A] text-sm mb-1">{match.partner.occupation}</p>
    )}
    <p className="text-[#666] text-xs mb-6">{match.event_title}</p>

    {match.interaction_data?.mutual_positive && (
      <p className="text-[#8B1A1A] text-xs mb-6 italic">Tuvieron alta afinidad conversacional</p>
    )}

    <p className="text-[#F5F0E6] text-lg font-serif mb-8">
      ¿Te gustaría seguir explorando esta conexión?
    </p>

    <div className="space-y-3">
      <button
        onClick={() => onConfirm(match.match_id, 'yes')}
        disabled={loading}
        className="btn-wine w-full flex items-center justify-center gap-3"
        data-testid={`confirm-yes-${match.match_id}`}
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
        Sí, me interesa
      </button>
      <button
        onClick={() => onConfirm(match.match_id, 'not_yet')}
        disabled={loading}
        className="w-full py-3 border border-[#333] text-[#8A8A8A] hover:border-[#555] hover:text-[#F5F0E6] transition-colors flex items-center justify-center gap-2"
        data-testid={`confirm-notyet-${match.match_id}`}
      >
        <Clock size={16} />
        Aún no estoy seguro/a
      </button>
      <button
        onClick={() => onConfirm(match.match_id, 'no')}
        disabled={loading}
        className="w-full py-3 text-[#555] hover:text-[#8A8A8A] transition-colors text-sm"
        data-testid={`confirm-no-${match.match_id}`}
      >
        No, gracias
      </button>
    </div>
  </div>
);

// ============== CONTACT UNLOCK CONSENT MODAL ==============
const ContactConsentModal = ({ match, onConfirm, onCancel, loading }) => (
  <div className="fixed inset-0 z-50 bg-[#0B0B0B]/90 backdrop-blur-md flex items-center justify-center px-6" data-testid="contact-consent-modal">
    <div className="max-w-sm w-full card-premium p-8 text-center">
      <div className="w-14 h-14 mx-auto mb-6 rounded-full bg-[#C4A76B]/15 flex items-center justify-center">
        <Unlock size={24} className="text-[#C4A76B]" />
      </div>
      
      <p className="text-[#8A8A8A] text-sm mb-6 italic leading-relaxed">
        Si ambos lo deciden, esta conexion puede salir de aqui.
      </p>
      
      <h3 className="text-xl font-serif text-[#F5F0E6] mb-3">Desbloquear contacto</h3>
      <p className="text-[#666] text-sm mb-2 leading-relaxed">
        Al confirmar, tu informacion de contacto sera visible para <span className="text-[#F5F0E6]">{match.partner?.first_name}</span>.
      </p>
      <p className="text-[#555] text-xs mb-8">
        Ambos deben aprobar para que el intercambio suceda.
      </p>
      
      {match.other_contact_requested && (
        <div className="mb-6 px-4 py-2 bg-[#C4A76B]/10 border border-[#C4A76B]/20 text-xs text-[#C4A76B]">
          {match.partner?.first_name} ya aprobo el intercambio
        </div>
      )}
      
      <button
        onClick={onConfirm}
        disabled={loading}
        className="btn-wine w-full flex items-center justify-center gap-2 mb-3"
        data-testid="consent-confirm-btn"
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
        Si, compartir mi contacto
      </button>
      <button
        onClick={onCancel}
        className="w-full py-3 text-[#666] text-sm hover:text-[#8A8A8A] transition-colors"
        data-testid="consent-cancel-btn"
      >
        Ahora no
      </button>
    </div>
  </div>
);

// ============== ICE BREAKER SUGGESTIONS ==============
const IceBreakerSuggestions = ({ suggestions, onUse, matchId }) => {
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [currentSuggestions, setCurrentSuggestions] = useState(suggestions);

  useEffect(() => { setCurrentSuggestions(suggestions); }, [suggestions]);

  if ((!currentSuggestions || currentSuggestions.length === 0) && !regenerating) return null;

  const sendFeedback = async (action, text) => {
    try {
      const res = await axios.post(`${API}/events/matches/${matchId}/ice-breaker-feedback`, {
        action, suggestion_text: text
      }, { withCredentials: true });
      
      if (action === 'regenerate' && res.data.suggestions) {
        setCurrentSuggestions(res.data.suggestions);
        setRegenerating(false);
      } else {
        setFeedbackSent(true);
      }
    } catch (e) { console.error(e); }
  };

  return (
    <div className="mx-4 mb-4 mt-2" data-testid="ice-breaker-suggestions">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={14} className="text-[#C4A76B]" />
        <p className="text-[#C4A76B] text-xs tracking-wide">Sugerencias para romper el hielo</p>
      </div>
      {regenerating ? (
        <div className="flex justify-center py-4">
          <Loader2 size={18} className="animate-spin text-[#C4A76B]" />
        </div>
      ) : (
        <div className="space-y-2">
          {currentSuggestions.map((suggestion, idx) => (
            <button
              key={idx}
              onClick={() => { onUse(suggestion); sendFeedback('used', suggestion); }}
              className="w-full text-left px-4 py-3 bg-[#111] border border-[#C4A76B]/15 hover:border-[#C4A76B]/40 text-[#8A8A8A] text-sm transition-all group"
              data-testid={`ice-breaker-${idx}`}
            >
              <span className="group-hover:text-[#F5F0E6] transition-colors">{suggestion}</span>
              <span className="block text-[#555] text-xs mt-1 group-hover:text-[#C4A76B] transition-colors">Puedes ajustarlo antes de enviarlo</span>
            </button>
          ))}
        </div>
      )}
      {/* Feedback row — optional, not intrusive */}
      {!feedbackSent && !regenerating && currentSuggestions.length > 0 && (
        <div className="flex justify-center gap-6 mt-3" data-testid="ice-breaker-feedback">
          <button
            onClick={() => sendFeedback('dismissed', currentSuggestions[0])}
            className="text-[#555] text-xs hover:text-[#C4A76B] transition-colors"
            data-testid="ice-breaker-helped-btn"
          >
            Me ayudo
          </button>
          <span className="text-[#333]">|</span>
          <button
            onClick={() => { setRegenerating(true); sendFeedback('regenerate', currentSuggestions[0]); }}
            className="text-[#555] text-xs hover:text-[#C4A76B] transition-colors"
            data-testid="ice-breaker-other-btn"
          >
            Prefiero otra sugerencia
          </button>
        </div>
      )}
      {feedbackSent && (
        <p className="text-center text-[#555] text-xs mt-2">Gracias</p>
      )}
    </div>
  );
};

// ============== UPGRADE PROMPT (ELEGANT) ==============
const UpgradePrompt = ({ message, onActivateTrial, trialAvailable }) => (
  <div className="mx-4 my-4 p-5 bg-gradient-to-b from-[#111] to-[#0B0B0B] border border-[#C4A76B]/20" data-testid="upgrade-prompt">
    <p className="text-[#F5F0E6] text-sm font-serif text-center mb-3">{message}</p>
    {trialAvailable && (
      <button
        onClick={onActivateTrial}
        className="w-full py-3 bg-[#C4A76B]/10 border border-[#C4A76B]/25 text-[#C4A76B] text-sm hover:bg-[#C4A76B]/20 transition-colors"
        data-testid="activate-trial-btn"
      >
        Probar Premium 7 dias
      </button>
    )}
    {!trialAvailable && (
      <p className="text-[#666] text-xs text-center">Proximamente: membresia premium</p>
    )}
  </div>
);

// ============== CHAT VIEW ==============
const ChatView = ({ match, onBack, onRequestContact, userMembership }) => {
  const [messages, setMessages] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contactStatus, setContactStatus] = useState(null);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);
  const [iceBreakers, setIceBreakers] = useState([]);
  const [iceBreakersLoaded, setIceBreakersLoaded] = useState(false);
  const [membershipBlock, setMembershipBlock] = useState(null);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/events/matches/${match.match_id}/messages`, { withCredentials: true });
      setMessages(res.data.messages || []);
    } catch (err) {
      if (err.response?.status !== 403) console.error(err);
    } finally {
      setLoading(false);
    }
  }, [match.match_id]);

  useEffect(() => {
    fetchMessages();
    pollRef.current = setInterval(fetchMessages, 5000);
    return () => clearInterval(pollRef.current);
  }, [fetchMessages]);

  // Load ice breakers when chat has no messages
  useEffect(() => {
    if (!loading && messages.length === 0 && !iceBreakersLoaded) {
      axios.get(`${API}/events/matches/${match.match_id}/ice-breakers`, { withCredentials: true })
        .then(res => setIceBreakers(res.data.suggestions || []))
        .catch(() => {})
        .finally(() => setIceBreakersLoaded(true));
    }
  }, [loading, messages.length, match.match_id, iceBreakersLoaded]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!newMsg.trim() || sending) return;
    setSending(true);
    try {
      const res = await axios.post(`${API}/events/matches/${match.match_id}/messages`, {
        text: newMsg
      }, { withCredentials: true });
      
      // Check for membership limit response
      if (res.data?.error === 'membership_limit') {
        setMembershipBlock(res.data);
        setSending(false);
        return;
      }
      
      setNewMsg('');
      await fetchMessages();
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  const handleRequestContact = async () => {
    setShowConsentModal(true);
  };

  const confirmContactUnlock = async () => {
    setConsentLoading(true);
    try {
      const res = await axios.post(`${API}/events/matches/${match.match_id}/request-contact`, {}, { withCredentials: true });
      
      if (res.data?.membership_required) {
        setContactStatus(res.data);
        setShowConsentModal(false);
        return;
      }
      
      setContactStatus(res.data);
      setShowConsentModal(false);
      if (res.data.unlocked) {
        onRequestContact();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setConsentLoading(false);
    }
  };

  const handleActivateTrial = async () => {
    try {
      const res = await axios.post(`${API}/events/membership/activate-trial`, {}, { withCredentials: true });
      if (res.data?.tier) {
        setMembershipBlock(null);
        setContactStatus(null);
        window.location.reload();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleUseIceBreaker = (text) => {
    setNewMsg(text);
    setIceBreakers([]);
  };

  return (
    <div className="flex flex-col h-[600px] max-w-lg mx-auto" data-testid={`chat-view-${match.match_id}`}>
      {/* Consent Modal */}
      {showConsentModal && (
        <ContactConsentModal
          match={match}
          onConfirm={confirmContactUnlock}
          onCancel={() => setShowConsentModal(false)}
          loading={consentLoading}
        />
      )}

      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-[#8B1A1A]/10">
        <button onClick={onBack} className="text-[#8A8A8A] hover:text-[#F5F0E6] transition-colors" data-testid="chat-back-btn">
          <X size={20} />
        </button>
        <div className="text-center">
          <p className="text-[#F5F0E6] font-serif">{match.partner?.first_name}</p>
          <p className="text-[#8A8A8A] text-xs">{match.display?.subtitle}</p>
        </div>
        {match.level === 'chat_unlocked' && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleRequestContact}
              className="text-[#8B1A1A] hover:text-[#B82828] transition-colors relative group"
              title="Solicitar desbloqueo de contacto"
              data-testid="request-contact-btn"
            >
              <Unlock size={20} />
              {match.other_contact_requested && (
                <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-[#C4A76B] rounded-full" />
              )}
            </button>
          </div>
        )}
        {match.level === 'contact_unlocked' && (
          <span className="text-emerald-400" title="Contacto desbloqueado">
            <Unlock size={20} />
          </span>
        )}
      </div>

      {/* Contact unlock status */}
      {contactStatus && !contactStatus.unlocked && (
        <div className="px-4 py-3 bg-[#161616] border-b border-[#222] text-center text-sm">
          <p className="text-[#8A8A8A]">{contactStatus.message}</p>
        </div>
      )}

      {/* Contact info (if unlocked) */}
      {match.level === 'contact_unlocked' && match.partner?.email && (
        <div className="px-4 py-3 bg-[#8B1A1A]/10 border-b border-[#8B1A1A]/20 text-center">
          <p className="text-[#F5F0E6] text-sm font-serif mb-1">Contacto desbloqueado</p>
          <p className="text-[#8A8A8A] text-xs">{match.partner.name} &bull; {match.partner.email}</p>
          {match.partner.phone && <p className="text-[#8A8A8A] text-xs">{match.partner.phone}</p>}
        </div>
      )}

      {/* My contact request status */}
      {match.my_contact_requested && !match.other_contact_requested && match.level === 'chat_unlocked' && (
        <div className="px-4 py-2 bg-[#C4A76B]/5 border-b border-[#C4A76B]/10 text-center">
          <p className="text-[#C4A76B] text-xs">Tu solicitud fue enviada. Esperando aprobacion.</p>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4" data-testid="chat-messages">
        {loading ? (
          <div className="flex justify-center py-10">
            <Loader2 size={24} className="animate-spin text-[#8B1A1A]" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-6">
            <Shield className="w-10 h-10 text-[#333] mx-auto mb-4" />
            <p className="text-[#8A8A8A] text-sm">Conversacion segura</p>
            <p className="text-[#555] text-xs mt-1 mb-4">Los mensajes son privados. Sin enlaces ni datos de contacto.</p>
            <IceBreakerSuggestions suggestions={iceBreakers} onUse={handleUseIceBreaker} matchId={match.match_id} />
          </div>
        ) : (
          messages.map(msg => (
            <div
              key={msg.message_id}
              className={`flex ${msg.sender_id === match.partner?.user_id ? 'justify-start' : 'justify-end'}`}
            >
              <div className={`max-w-[75%] px-4 py-3 text-sm ${
                msg.sender_id === match.partner?.user_id
                  ? 'bg-[#161616] border border-[#222] text-[#F5F0E6]'
                  : 'bg-[#8B1A1A]/20 border border-[#8B1A1A]/30 text-[#F5F0E6]'
              }`}>
                <p>{msg.text}</p>
                <p className="text-[#555] text-xs mt-1">
                  {new Date(msg.sent_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}
                </p>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Membership block message */}
      {membershipBlock && (
        <UpgradePrompt
          message={membershipBlock.message}
          onActivateTrial={handleActivateTrial}
          trialAvailable={membershipBlock.current_tier === 'free'}
        />
      )}

      {/* Contact membership block */}
      {contactStatus?.membership_required && (
        <UpgradePrompt
          message={contactStatus.message}
          onActivateTrial={handleActivateTrial}
          trialAvailable={contactStatus.current_tier === 'free'}
        />
      )}

      {/* Input */}
      {!membershipBlock && (
        <div className="p-4 border-t border-[#8B1A1A]/10">
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={newMsg}
              onChange={e => setNewMsg(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSend()}
              placeholder="Escribe un mensaje..."
              maxLength={500}
              className="flex-1 bg-[#111] border border-[#333] text-[#F5F0E6] px-4 py-3 text-sm focus:outline-none focus:border-[#8B1A1A] transition-colors placeholder:text-[#555]"
              data-testid="chat-input"
            />
            <button
              onClick={handleSend}
              disabled={!newMsg.trim() || sending}
              className={`w-12 h-12 flex items-center justify-center bg-[#8B1A1A] text-[#F5F0E6] transition-opacity ${
                !newMsg.trim() ? 'opacity-30' : 'hover:opacity-80'
              }`}
              data-testid="chat-send-btn"
            >
              {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== MATCH CARD (tier-aware) ==============
const EvolutiveMatchCard = ({ match, onOpenChat, onConfirm, confirmLoading }) => {
  const levelConfig = {
    mutual: {
      border: 'border-[#8B1A1A]/30',
      badge: 'bg-[#8B1A1A]/15 text-[#8B1A1A]',
      icon: Heart
    },
    chat_unlocked: {
      border: 'border-emerald-500/20',
      badge: 'bg-emerald-900/20 text-emerald-400',
      icon: MessageCircle
    },
    contact_unlocked: {
      border: 'border-[#C4A76B]/30',
      badge: 'bg-[#C4A76B]/15 text-[#C4A76B]',
      icon: Unlock
    }
  };

  const cfg = levelConfig[match.level] || levelConfig.mutual;
  const Icon = cfg.icon;
  const chatQuality = match.chat_quality;

  return (
    <div className={`card-premium p-6 border ${cfg.border} ${match.focus_suggestion ? 'ring-1 ring-[#C4A76B]/30' : ''}`} data-testid={`match-card-${match.match_id}`}>
      {/* Focus suggestion badge */}
      {match.focus_suggestion && (
        <div className="flex items-center gap-2 mb-4 px-3 py-1.5 bg-[#C4A76B]/8 border border-[#C4A76B]/15 text-xs text-[#C4A76B]" data-testid="focus-suggestion-badge">
          <Star size={12} />
          {match.focus_suggestion}
        </div>
      )}

      {/* Header */}
      <div className="flex items-start justify-between mb-5">
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#8B1A1A] to-[#5a1111] flex items-center justify-center text-xl font-serif text-[#F5F0E6]">
            {match.partner?.first_name?.[0] || '?'}
          </div>
          <div>
            <h3 className="text-lg font-serif text-[#F5F0E6]">{match.partner?.first_name}</h3>
            {match.partner?.occupation && (
              <p className="text-[#8A8A8A] text-xs">{match.partner.occupation}</p>
            )}
          </div>
        </div>
        <div className={`flex items-center gap-1.5 px-3 py-1 text-xs ${cfg.badge}`}>
          <Icon size={12} />
          {match.display?.title}
        </div>
      </div>

      {/* Match context */}
      <p className="text-[#666] text-xs mb-4">{match.event_title}</p>
      <p className="text-[#8A8A8A] text-sm italic mb-5">{match.display?.subtitle}</p>

      {/* Chat quality indicator (subtle) */}
      {chatQuality && match.level === 'chat_unlocked' && (
        <div className="mb-4">
          {chatQuality.signals?.filter(s => s.severity === 'critical' || s.severity === 'warning').map((signal, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-amber-500/70 mb-1" data-testid={`chat-quality-signal-${i}`}>
              <AlertTriangle size={12} />
              {signal.detail}
            </div>
          ))}
        </div>
      )}

      {/* Contact request status on card */}
      {match.other_contact_requested && !match.my_contact_requested && match.level === 'chat_unlocked' && (
        <div className="mb-4 flex items-center gap-2 text-[#C4A76B] text-xs" data-testid="other-contact-requested">
          <Unlock size={12} />
          {match.partner?.first_name} quiere intercambiar contacto
        </div>
      )}

      {/* Unread badge */}
      {match.unread_messages > 0 && (
        <div className="mb-4 flex items-center gap-2 text-[#8B1A1A] text-xs">
          <MessageCircle size={14} />
          {match.unread_messages} mensaje{match.unread_messages > 1 ? 's' : ''} sin leer
        </div>
      )}

      {/* Actions based on level */}
      {match.needs_my_confirmation ? (
        <div className="space-y-2">
          <button
            onClick={() => onConfirm(match.match_id, 'yes')}
            disabled={confirmLoading}
            className="btn-wine w-full flex items-center justify-center gap-2"
            data-testid={`card-confirm-yes-${match.match_id}`}
          >
            {confirmLoading ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
            Sí, seguir explorando
          </button>
          <div className="flex gap-2">
            <button
              onClick={() => onConfirm(match.match_id, 'not_yet')}
              className="flex-1 py-2 border border-[#333] text-[#8A8A8A] text-xs hover:border-[#555] transition-colors"
              data-testid={`card-confirm-notyet-${match.match_id}`}
            >
              Aún no
            </button>
            <button
              onClick={() => onConfirm(match.match_id, 'no')}
              className="flex-1 py-2 text-[#555] text-xs hover:text-[#8A8A8A] transition-colors"
              data-testid={`card-confirm-no-${match.match_id}`}
            >
              Declinar
            </button>
          </div>
        </div>
      ) : match.waiting_for_other ? (
        <div className="flex items-center gap-2 text-[#8A8A8A] text-sm py-3">
          <Clock size={16} className="text-[#555]" />
          Esperando respuesta...
        </div>
      ) : match.level === 'chat_unlocked' || match.level === 'contact_unlocked' ? (
        <button
          onClick={() => onOpenChat(match)}
          className="btn-wine w-full flex items-center justify-center gap-2"
          data-testid={`open-chat-${match.match_id}`}
        >
          <MessageCircle size={16} />
          {match.display?.action_text}
        </button>
      ) : (
        <div className="flex items-center gap-2 text-[#555] text-sm py-3">
          <Lock size={16} />
          Pendiente de confirmación mutua
        </div>
      )}
    </div>
  );
};

// ============== MAIN MATCHES TAB ==============
const EvolutiveMatchesTab = ({ matches: initialMatches }) => {
  const [matches, setMatches] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [membership, setMembership] = useState({ tier: 'free', limits: {} });

  const fetchMatches = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/events/matches/me`, { withCredentials: true });
      setMatches(res.data || []);
    } catch (err) {
      console.error('Error fetching matches:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMatches();
    axios.get(`${API}/events/membership/me`, { withCredentials: true })
      .then(res => setMembership(res.data))
      .catch(() => {});
  }, [fetchMatches]);

  const handleConfirm = async (matchId, response) => {
    setConfirmLoading(true);
    try {
      await axios.post(`${API}/events/matches/${matchId}/confirm`, { response }, { withCredentials: true });
      await fetchMatches();
    } catch (err) {
      console.error('Confirm error:', err);
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleOpenChat = (match) => {
    setActiveChat(match);
  };

  // If chat is open, show chat view
  if (activeChat) {
    return (
      <ChatView
        match={activeChat}
        onBack={() => { setActiveChat(null); fetchMatches(); }}
        onRequestContact={() => fetchMatches()}
        userMembership={membership}
      />
    );
  }

  // Group matches by level
  const needsConfirmation = matches.filter(m => m.needs_my_confirmation);
  const activeConnections = matches.filter(m => 
    (m.level === 'chat_unlocked' || m.level === 'contact_unlocked') && !m.needs_my_confirmation
  );
  const waitingMatches = matches.filter(m => m.waiting_for_other);

  return (
    <div data-testid="evolutive-matches-tab">
      <div className="mb-8">
        <h2 className="text-2xl font-serif text-[#F5F0E6] mb-3">Mis Conexiones</h2>
        <p className="text-[#8A8A8A]">
          Las conexiones evolucionan progresivamente. Cada nivel acerca más a quienes realmente conectaron.
        </p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20">
          <Loader2 size={32} className="animate-spin text-[#8B1A1A]" />
        </div>
      ) : matches.length === 0 ? (
        <div className="card-premium p-16 text-center">
          <Heart className="w-16 h-16 text-[#8B1A1A] mx-auto mb-6 opacity-30" />
          <h3 className="text-2xl font-serif text-[#F5F0E6] mb-4">Sin conexiones aún</h3>
          <p className="text-[#8A8A8A] max-w-md mx-auto">
            Los matches surgen después de experiencias presenciales. Cuando ambas personas se seleccionan mutuamente, la conexión se revela.
          </p>
        </div>
      ) : (
        <div className="space-y-10">
          {/* Needs confirmation */}
          {needsConfirmation.length > 0 && (
            <div>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-2 h-2 bg-[#8B1A1A] rounded-full animate-pulse" />
                <h3 className="text-lg font-serif text-[#F5F0E6]">Requieren tu respuesta</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {needsConfirmation.map(m => (
                  <EvolutiveMatchCard
                    key={m.match_id}
                    match={m}
                    onOpenChat={handleOpenChat}
                    onConfirm={handleConfirm}
                    confirmLoading={confirmLoading}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Active connections */}
          {activeConnections.length > 0 && (
            <div>
              <h3 className="text-lg font-serif text-[#F5F0E6] mb-5">Conexiones activas</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {activeConnections.map(m => (
                  <EvolutiveMatchCard
                    key={m.match_id}
                    match={m}
                    onOpenChat={handleOpenChat}
                    onConfirm={handleConfirm}
                    confirmLoading={confirmLoading}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Waiting for other */}
          {waitingMatches.length > 0 && (
            <div>
              <h3 className="text-lg font-serif text-[#F5F0E6] mb-5">Esperando respuesta</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {waitingMatches.map(m => (
                  <EvolutiveMatchCard
                    key={m.match_id}
                    match={m}
                    onOpenChat={handleOpenChat}
                    onConfirm={handleConfirm}
                    confirmLoading={confirmLoading}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Level progress legend */}
          <div className="card-premium p-6">
            <p className="text-[#8A8A8A] text-xs uppercase tracking-wider mb-4">Cómo evolucionan las conexiones</p>
            <div className="flex flex-wrap items-center gap-3 text-xs">
              <div className="flex items-center gap-2 text-[#8B1A1A]">
                <Heart size={14} />
                Conexión mutua
              </div>
              <ChevronRight size={12} className="text-[#333]" />
              <div className="flex items-center gap-2 text-emerald-400">
                <MessageCircle size={14} />
                Conversación
              </div>
              <ChevronRight size={12} className="text-[#333]" />
              <div className="flex items-center gap-2 text-[#C4A76B]">
                <Unlock size={14} />
                Contacto
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EvolutiveMatchesTab;
